/**
 * Razorpay Checkout chassis.
 *
 * v0:
 *  - lazy-loads https://checkout.razorpay.com/v1/checkout.js
 *  - opens checkout for the chosen tier
 *  - on capture, persists "entitlement" to localStorage (server-side
 *    verification deferred to v0.2 — the captured-payment webhook at
 *    taxwallaai.com is already registered and signs receipts).
 *
 * Anti-fraud note: localStorage entitlement is a soft gate, not a security
 * boundary. The vault itself is purely on-device and the paywall is a
 * conscience-tax for premium features (handover PDF, family sharing).
 */

export type Tier = 'free' | 'pro' | 'family' | 'bundle';

export interface PricingTier {
  id: Tier;
  name: string;
  hindi: string;
  monthly_paise: number;
  yearly_paise: number;
  pitch: string;
  perks: string[];
}

export const PRICING: PricingTier[] = [
  {
    id: 'free',
    name: 'Free',
    hindi: 'मुफ्त',
    monthly_paise: 0,
    yearly_paise: 0,
    pitch: 'The vault itself. Forever.',
    perks: [
      'Asset vault (unlimited entries)',
      'Up to 2 nominees',
      'Last wishes (up to 5 entries)',
      '50 MB document vault',
      'Manual export only',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    hindi: 'प्रो',
    monthly_paise: 9_900,
    yearly_paise: 99_900,
    pitch: 'Dead-man-switch + Shamir + family handover PDF.',
    perks: [
      'Everything in Free',
      'Unlimited nominees',
      'Dead-man-switch with Shamir 3-of-5',
      'Crypto seed vault with threshold backup',
      'Family handover PDF generator',
      '1 GB document vault',
      'Voice + camera capture',
      'WebAuthn passkey unlock',
      'Apple Intelligence iOS 27 MCP',
    ],
  },
  {
    id: 'family',
    name: 'Family',
    hindi: 'परिवार',
    monthly_paise: 24_900,
    yearly_paise: 299_900,
    pitch: 'Up to 6 vaults. One parchment. One pricing.',
    perks: [
      'Everything in Pro',
      'Up to 6 vault holders (spouse, parents, in-laws, children)',
      '5 GB shared document vault',
      'Cross-vault visibility (read-only, opt-in)',
      'Family-tree heir graph',
      'Priority support (48 hr SLA)',
    ],
  },
  {
    id: 'bundle',
    name: 'Empire Bundle',
    hindi: 'साम्राज्य',
    monthly_paise: 29_900,
    yearly_paise: 299_900,
    pitch: 'Wasiyat plus seven other Empire apps. 62% off.',
    perks: [
      'Wasiyat Family tier',
      'Hisaab + Khazaana + Tankhwah (money trinity)',
      'Aramgah + Sehat (body and mind)',
      'Almari + Dial (life inventory)',
      'Shared chassis updates',
      'Single Empire passcode (optional)',
    ],
  },
];

interface RazorpayOptions {
  key: string;
  amount: number;
  currency: 'INR';
  name: string;
  description: string;
  prefill?: { name?: string; email?: string; contact?: string };
  notes?: Record<string, string>;
  theme?: { color?: string };
  handler?: (response: { razorpay_payment_id: string; razorpay_order_id?: string; razorpay_signature?: string }) => void;
  modal?: { ondismiss?: () => void };
}

interface RazorpayInstance {
  open(): void;
  close(): void;
}

declare global {
  interface Window {
    Razorpay?: new (opts: RazorpayOptions) => RazorpayInstance;
  }
}

// Razorpay publishable key — safe in client bundle by convention (conscience-tax model).
// Publishable key_id is NOT a secret; hardcoded here per SHARED.md spec.
const RZP_KEY = 'rzp_live_SVzHpgz5EgB4bk';
const ENTITLEMENT_KEY = 'wasiyat:entitlement';

interface Entitlement {
  tier: Tier;
  granted_at: string;
  expires_at: string;
  payment_id: string;
}

async function loadCheckoutScript(): Promise<boolean> {
  if (window.Razorpay) return true;
  return new Promise<boolean>((resolve) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.head.appendChild(script);
  });
}

export async function openCheckout(tier: Tier, billing: 'monthly' | 'yearly', ownerName: string): Promise<boolean> {
  if (tier === 'free') {
    grantEntitlement('free', 'free-tier');
    return true;
  }
  const ok = await loadCheckoutScript();
  if (!ok || !window.Razorpay) return false;
  const conf = PRICING.find((p) => p.id === tier);
  if (!conf) return false;
  const amount = billing === 'monthly' ? conf.monthly_paise : conf.yearly_paise;
  return new Promise<boolean>((resolve) => {
    const rzp = new (window.Razorpay as NonNullable<typeof window.Razorpay>)({
      key: RZP_KEY,
      amount,
      currency: 'INR',
      name: 'Wasiyat',
      description: `${conf.name} (${billing}) — by Aditya`,
      prefill: { name: ownerName },
      notes: { brand: 'wasiyat', tier, billing },
      theme: { color: '#2D3964' },
      handler: (response) => {
        const days = billing === 'monthly' ? 31 : 366;
        grantEntitlement(tier, response.razorpay_payment_id, days);
        resolve(true);
      },
      modal: { ondismiss: () => resolve(false) },
    });
    rzp.open();
  });
}

export function grantEntitlement(tier: Tier, paymentId: string, days = 366): void {
  const now = new Date();
  const expires = new Date(now.getTime() + days * 86_400_000);
  const ent: Entitlement = {
    tier,
    granted_at: now.toISOString(),
    expires_at: expires.toISOString(),
    payment_id: paymentId,
  };
  localStorage.setItem(ENTITLEMENT_KEY, JSON.stringify(ent));
}

export function currentEntitlement(): Entitlement {
  const raw = localStorage.getItem(ENTITLEMENT_KEY);
  if (!raw) return { tier: 'free', granted_at: new Date().toISOString(), expires_at: new Date(Date.now() + 365 * 86_400_000).toISOString(), payment_id: 'free-tier' };
  try {
    const ent = JSON.parse(raw) as Entitlement;
    const expires = new Date(ent.expires_at).getTime();
    if (Date.now() > expires) {
      return { tier: 'free', granted_at: new Date().toISOString(), expires_at: new Date(Date.now() + 365 * 86_400_000).toISOString(), payment_id: 'expired' };
    }
    return ent;
  } catch {
    return { tier: 'free', granted_at: new Date().toISOString(), expires_at: new Date(Date.now() + 365 * 86_400_000).toISOString(), payment_id: 'invalid' };
  }
}

export function hasFeature(tier: Tier, feature: 'dms' | 'shamir' | 'handover_pdf' | 'voice' | 'camera' | 'family_share' | 'mcp'): boolean {
  const free = new Set<typeof feature>(['voice', 'camera']);
  const pro = new Set<typeof feature>(['dms', 'shamir', 'handover_pdf', 'voice', 'camera', 'mcp']);
  const family = new Set<typeof feature>(['dms', 'shamir', 'handover_pdf', 'voice', 'camera', 'family_share', 'mcp']);
  if (tier === 'free') return free.has(feature);
  if (tier === 'pro') return pro.has(feature);
  return family.has(feature);
}
